import { Navigate, NavLink, Outlet } from "react-router-dom";
import {
  Building2,
  CreditCard,
  LayoutDashboard,
  LogOut,
  SlidersHorizontal,
} from "lucide-react";
import { cn } from "../lib/cn";
import { useBrands } from "../lib/brand";
import { useLogout, useSessionUser } from "../lib/session";
import { Badge } from "./ui/badge";
import { Brandmark } from "./Brandmark";

interface SideItem {
  to: string;
  label: string;
  icon: typeof Building2;
  soon?: boolean;
}

const MASTER_DATA: SideItem[] = [
  { to: "/brands", label: "Brand", icon: Building2 },
  { to: "/payments", label: "Payment", icon: CreditCard, soon: true },
];

/**
 * The admin CONSOLE shell: a left sidebar for management (Dashboard, Master Data,
 * Pengaturan). Distinct from the per-brand Workspace. Admin-only — other roles
 * are bounced to their workspace.
 */
export function ConsoleLayout() {
  const user = useSessionUser();
  const { isAdmin } = useBrands();
  const logout = useLogout();

  if (!isAdmin) return <Navigate to="/drafts" replace />;

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      "flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors",
      isActive
        ? "bg-teal/15 text-teal"
        : "text-slate-600 hover:bg-slate-100 hover:text-navy",
    );

  const groupLabel =
    "px-3 pb-1 pt-5 text-xs font-semibold uppercase tracking-wider text-slate-400";

  return (
    <div className="flex min-h-full">
      <aside className="flex w-60 shrink-0 flex-col border-r border-slate-200 bg-white">
        <div className="px-5 py-4">
          <Brandmark variant="sidebar" />
          <p className="mt-2 text-xs text-slate-400">Admin Console</p>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto px-3 pb-4">
          <NavLink to="/dashboard" className={linkClass}>
            <LayoutDashboard className="h-4 w-4" />
            Dashboard
          </NavLink>

          <p className={groupLabel}>Master Data</p>
          {MASTER_DATA.map(({ to, label, icon: Icon, soon }) => (
            <NavLink key={to} to={to} className={linkClass}>
              <Icon className="h-4 w-4" />
              {label}
              {soon ? (
                <Badge variant="secondary" className="ml-auto text-[10px]">
                  segera
                </Badge>
              ) : null}
            </NavLink>
          ))}

          <p className={groupLabel}>Pengaturan</p>
          <NavLink to="/settings" className={linkClass}>
            <SlidersHorizontal className="h-4 w-4" />
            Pengaturan global
          </NavLink>
        </nav>

        <div className="border-t border-slate-200 p-3">
          <p className="truncate px-1 text-sm font-medium text-navy">
            {user.email}
          </p>
          <div className="mt-1.5 flex items-center justify-between px-1">
            <Badge variant="teal">{user.role}</Badge>
            <button
              type="button"
              onClick={() => logout.mutate()}
              disabled={logout.isPending}
              className="flex items-center gap-1.5 rounded-md px-2 py-1 text-sm text-slate-500 transition-colors hover:bg-slate-100 hover:text-navy disabled:opacity-50"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      </aside>

      <main className="min-w-0 flex-1 bg-slate-50">
        <div className="mx-auto max-w-5xl px-6 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
